"use client";

import { useMemo, useRef } from "react";
import {
  localSlotIdOfThreadKey,
  localSlotMatches,
  splitLocalKeyId,
  welcomeStateKey,
} from "@/lib/targeting/local-slots";
import { useLocalSlots } from "@/lib/targeting/local-slots-context";
import { pickVariant } from "@/lib/targeting/match";
import { pinThreadVariant } from "@/lib/targeting/pin-thread-variant";
import { useSessionContentContext } from "@/lib/targeting/session-content-context";
import { type SlotPick, slotIdOfThreadKey } from "@/lib/targeting/slot-thread";
import { useMatchContext } from "@/lib/targeting/use-match-context";
import type { MessageSlot, MessageVariant } from "@/lib/widget-config";
import { type FrozenPicks, resolveWelcomes, type WelcomePick } from "./resolve-welcomes";

export interface WelcomeContent {
  /** What each slot shows now. Empty while `held`, apart from SYSTEM. */
  welcomes: WelcomePick[];
  /** The session content is still pending and a MESSAGE welcome is waiting behind the typing dots. */
  held: boolean;
  /**
   * In a `message:<slotId>` thread, the variant shown (the first send carries it as `messageSlot`),
   * or the page's variant while the session answer is pending; `null` in every other thread.
   */
  slotPick: SlotPick | null;
  /**
   * In a REGULAR thread that begins with a page's slot text (`welcomeSlotId`), the variant shown: its
   * first send carries it as `messageSlot` so the BE seeds the same text. `null` otherwise.
   */
  welcomePick: SlotPick | null;
  /**
   * In a LOCAL slot thread (`slot-<id>`, a slot the BE does not know), the slot and variant its header
   * shows: the first send persists it as the thread's snapshot. `null` in every other thread.
   */
  localHeader: { slotId: string; variant: MessageVariant } | null;
  /** Every MESSAGE-slot variant text: the BE thread seed of ANY thread is one of them. */
  seedTexts: readonly { text: string }[];
}

/**
 * Welcome / hint / system messages from contract-v1 slots: the session projection replaces the
 * public one, the first matching variant wins, MESSAGE and HINT freeze per thread at the first
 * reveal and SYSTEM re-picks on navigation. The freeze map is keyed by visitor token + thread so a
 * different person on a shared device never inherits a frozen variant.
 */
export function useWelcomeContent(opts: {
  /** Public `messenger.messageSlots` (contract v1). */
  publicSlots: readonly MessageSlot[];
  /** The config uses contract-v1 slots (`messenger.messageSlots` present). */
  active: boolean;
  isOpen: boolean;
  threadKey: string | null | undefined;
  /**
   * A regular new thread started on a page shows this MESSAGE slot's text and quick replies client-side
   * (not persisted, no seed, no pick). Ignored in a `message:` thread, which shows its own slot.
   */
  welcomeSlotId?: string;
  token: string | null;
}): WelcomeContent {
  const { publicSlots, active, isOpen, threadKey, token, welcomeSlotId } = opts;
  const session = useSessionContentContext();
  const ctx = useMatchContext();
  const frozenRef = useRef(new Map<string, FrozenPicks>());

  const allSlots = session.slots?.messageSlots ?? publicSlots;
  // A `message:<slot>:<variant>` thread shows its own variant, not what the page matches now.
  const beSlots = useMemo(() => pinThreadVariant(allSlots, threadKey), [allSlots, threadKey]);

  // A LOCAL slot thread (`slot-<id>`) renders its header client-side. Once the first send persisted a
  // snapshot, the header is that snapshot (frozen, and it outlives the slot leaving the config).
  const local = useLocalSlots();
  const localKeyId = localSlotIdOfThreadKey(threadKey);
  const snapshot = localKeyId ? local.snapshotOf(localKeyId) : undefined;
  const slots = useMemo(() => {
    if (!localKeyId || !snapshot) return beSlots;
    const own = beSlots.find((x) => x.kind === "MESSAGE" && localSlotMatches(x.id, localKeyId));
    const frozenSlot: MessageSlot = {
      ...own,
      id: own?.id ?? splitLocalKeyId(localKeyId).slotKeyId,
      kind: "MESSAGE",
      variants: [snapshot],
    };
    return [...beSlots.filter((x) => x !== own), frozenSlot];
  }, [beSlots, localKeyId, snapshot]);
  const localSlotId =
    localKeyId == null
      ? undefined
      : slots.find((x) => x.kind === "MESSAGE" && localSlotMatches(x.id, localKeyId))?.id;

  // The freeze key follows the PERSON, not the token: a handshake that lands after the token wait
  // (null -> token) must keep what is already frozen, while a different person (token A -> B or
  // logout) starts clean so nothing frozen for the previous visitor is shown.
  const personRef = useRef({ token: null as string | null, epoch: 0 });
  if (personRef.current.token !== token) {
    if (personRef.current.token !== null) personRef.current.epoch += 1;
    personRef.current.token = token;
  }
  const freezeKey = `${personRef.current.epoch}|${welcomeStateKey(threadKey)}`;
  let frozen = frozenRef.current.get(freezeKey);
  if (!frozen) {
    frozen = new Map();
    frozenRef.current.set(freezeKey, frozen);
  }

  // Hold while the session answer is pending: the answer may bring (or replace) a MESSAGE welcome,
  // so a welcome that only exists in the session projection must not pop in afterwards either. A
  // MESSAGE welcome already frozen for this thread is never hidden again.
  const hasFrozenMessage = slots.some((s) => s.kind === "MESSAGE" && frozen.has(s.id));
  const held = active && !session.settled && !hasFrozenMessage && !snapshot;

  const threadSlotId = slotIdOfThreadKey(threadKey) ?? undefined;
  const messageSlotId = threadSlotId ?? localSlotId ?? welcomeSlotId;
  const welcomes = useMemo(
    () =>
      resolveWelcomes(slots, ctx, frozen, isOpen && !held, {
        hold: held,
        messageSlotId,
        excludeMessages: active && !messageSlotId,
      }),
    [slots, ctx, frozen, isOpen, held, messageSlotId, active],
  );

  const slotPick = useMemo<SlotPick | null>(() => {
    if (!threadSlotId) return null;
    const own = welcomes.find((w) => w.slotId === threadSlotId);
    if (own) return { slotId: own.slotId, variantId: own.variant.id };
    // A send while the session answer is still pending must still carry a pick (the BE rejects a
    // `message:` key without one): the variant visible on this page (never an invisible one).
    const slot = slots.find((s) => s.kind === "MESSAGE" && s.id === threadSlotId);
    const variant = slot ? pickVariant(slot.variants, ctx) : null;
    return slot && variant ? { slotId: slot.id, variantId: variant.id } : null;
  }, [welcomes, threadSlotId, slots, ctx]);

  const localHeader = useMemo(() => {
    const own = localSlotId ? welcomes.find((w) => w.slotId === localSlotId) : undefined;
    return own ? { slotId: own.slotId, variant: own.variant } : null;
  }, [welcomes, localSlotId]);

  const welcomePick = useMemo<SlotPick | null>(() => {
    if (threadSlotId || !welcomeSlotId) return null;
    const own = welcomes.find((w) => w.kind === "MESSAGE" && w.slotId === welcomeSlotId);
    return own ? { slotId: own.slotId, variantId: own.variant.id } : null;
  }, [welcomes, threadSlotId, welcomeSlotId]);

  const seedTexts = useMemo(
    () =>
      slots.flatMap((sl) =>
        sl.kind === "MESSAGE" ? sl.variants.map((v) => ({ text: v.text })) : [],
      ),
    [slots],
  );

  return useMemo(
    () => ({ welcomes, held, slotPick, welcomePick, localHeader, seedTexts }),
    [welcomes, held, slotPick, welcomePick, localHeader, seedTexts],
  );
}
